import { useLivestreamSection } from "./hooks/useLivestreamSection";

import { LivestreamChatSlot } from "./LivestreamChatSlot";
import type { LiveStreamEntry } from "./hooks/useHomeStream";
import { CommonTwitchLink } from "../shared/CommonTwitchLink";

interface LiveStreamProps {
  matchSoon: boolean;
  enabled: boolean;
  streamChannel?: string | null;
  streamLink?: string;
  liveStreamEntries?: LiveStreamEntry[];
  hasLiveTaggedMatches?: boolean;
  visibilityPending?: boolean;
}

export const LiveStream = ({
  matchSoon,
  enabled,
  streamChannel,
  streamLink,
  liveStreamEntries = [],
  hasLiveTaggedMatches = false,
  visibilityPending = false,
}: LiveStreamProps) => {
  const {
    strings,
    shouldShow,
    isLive,
    expanded,
    chatOpen,
    isPoppedOut,
    popoutSupported,
    viewerLabel,
    showHeader,
    showLoadingHint,
    title,
    streamStatusPending,
    toggleChat,
    toggleExpanded,
    togglePopout,
    closePopout,
    hasMultipleEmbeds,
    embedEntries,
    renderedEmbedEntries,
    showDualLayout,
    primaryStreamLink,
    primaryStreamerLabel,
    openDualPopout,
    isDualChatHidden,
    toggleDualChat,
    dualViewerLabel,
  } = useLivestreamSection({
    matchSoon,
    enabled,
    streamChannel,
    streamLink,
    liveStreamEntries,
    hasLiveTaggedMatches,
    visibilityPending,
  });

  // Reserve a near-final shell while Twitch healthcheck runs in the match window
  // so the real player does not grow the page (CLS). Stay hidden until live.
  if (!shouldShow) {
    if (streamStatusPending && (matchSoon || hasLiveTaggedMatches)) {
      return (
        <section
          className="flex w-full items-center justify-center py-[clamp(0.75rem,2vw,1.25rem)]"
          aria-hidden="true"
          aria-busy="true"
        >
          <div className="box-border flex w-full max-w-[min(90rem,72rem)] flex-col items-center gap-[0.65rem] px-[var(--padding-view)]">
            <div className="h-[2.75rem] w-full max-w-[24rem] rounded bg-white/[0.04]" />
            <div className="livestream-inline-player aspect-video w-full bg-white/[0.03]" />
            <div className="h-[3.25rem] w-full rounded-[0.75rem] border border-white/[0.06] bg-white/[0.03]" />
            <div className="min-h-6 w-full" />
          </div>
        </section>
      );
    }
    return null;
  }

  const sectionExpanded =
    "fixed inset-0 z-[100001] w-screen max-w-[100vw] min-h-[100dvh] overflow-hidden bg-[#0b0b12] p-0";

  return (
    <section
      className={[
        "flex w-full items-center justify-center py-[clamp(0.75rem,2vw,1.25rem)]",
        expanded && sectionExpanded,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={title}
    >
      <div
        className={
          expanded
            ? "flex w-full h-full max-h-[100dvh] flex-col items-stretch gap-[0.5rem] p-[0.5rem] box-border"
            : `flex w-full ${hasMultipleEmbeds ? "max-w-[min(98rem,92rem)]" : "max-w-[min(90rem,72rem)]"} flex-col items-center justify-center gap-[0.65rem] px-[var(--padding-view)] box-border`
        }
      >
        {showHeader ? (
          <header
            className={
              expanded
                ? "flex w-full flex-col items-center gap-[0.35rem] text-center px-[0.25rem]"
                : "flex w-full max-w-[90rem] flex-col items-center gap-[0.35rem] text-center"
            }
          >
            <h2 className="home-section-title">{title}</h2>
            {isLive ? (
              <span className="inline-flex items-center shrink-0 px-[0.5rem] py-[0.15rem] rounded-full bg-red-500/[0.18] border border-red-500/[0.45] text-[#fca5a5] text-[0.72rem] font-bold tracking-[0.05em] uppercase">
                {strings.LIVE.LIVE_BADGE}
              </span>
            ) : null}
          </header>
        ) : null}

        <div
          className={[
            "flex w-full items-stretch gap-[0.75rem]",
            expanded && "flex-1 min-h-0 max-w-none",
            "max-[900px]:flex-col",
            chatOpen &&
              !showDualLayout &&
              expanded &&
              "max-[900px]:grid max-[900px]:grid-rows-[minmax(0,1fr)_minmax(10rem,28vh)] max-[900px]:gap-[0.65rem]",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {showDualLayout ? (
            <div className="grid w-full min-h-0 flex-1 grid-cols-2 gap-[0.75rem] max-[1100px]:grid-cols-1">
              {embedEntries.map((entry) => {
                const entryViewerLabel = dualViewerLabel(entry.channel);
                const entryChatHidden = isDualChatHidden(entry.channel);

                return (
                  <section key={entry.channel} className="relative flex min-h-0 w-full flex-1">
                    <div
                      className={`relative z-[2] flex h-full w-full min-h-0 flex-col gap-0 rounded-[0.7rem] bg-[rgba(15,23,42,0.72)] p-2 ${
                        expanded
                          ? "min-h-[clamp(22rem,58vh,36rem)]"
                          : "min-h-[clamp(18rem,42vh,26rem)]"
                      }`}
                    >
                      <div
                        className={`grid min-h-0 flex-1 gap-0 ${
                          entryChatHidden
                            ? "grid-cols-1"
                            : "grid-cols-[minmax(0,1fr)_clamp(14rem,24vw,20rem)] max-[900px]:grid-cols-1 max-[900px]:grid-rows-[minmax(0,1fr)_minmax(12rem,28vh)]"
                        }`}
                      >
                        <div className="relative min-h-0 overflow-hidden rounded-l-[0.55rem] rounded-r-none bg-black/45 max-[900px]:rounded-t-[0.55rem] max-[900px]:rounded-b-none">
                          <iframe
                            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                            src={entry.src}
                            title={`Finnhouse Live Stream ${entry.channel}`}
                            allow="autoplay; fullscreen"
                            className="absolute inset-0 h-full w-full border-none"
                          />
                        </div>
                        {!entryChatHidden ? (
                          <div className="relative min-h-0 overflow-hidden rounded-l-none rounded-r-[0.55rem] bg-[#0e0e10] max-[900px]:rounded-t-none max-[900px]:rounded-b-[0.55rem]">
                            <iframe
                              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                              src={entry.chatSrc}
                              title={`${strings.LIVE.CHAT_TITLE} ${entry.channel}`}
                              className="absolute inset-0 h-full w-full border-none"
                            />
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center justify-between gap-2 rounded-[0.55rem] border border-[rgba(44,137,225,0.25)] bg-[rgba(15,23,42,0.75)] px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="truncate font-jetbrains text-[0.78rem] uppercase tracking-[0.05em] text-white/70">
                            {entry.channel}
                          </span>
                          {entryViewerLabel ? (
                            <span className="shrink-0 text-[#c4b5fd] text-[0.72rem] font-semibold whitespace-nowrap">
                              {entryViewerLabel}
                            </span>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            className="livestream-action-btn text-[0.68rem]"
                            onClick={() => {
                              toggleDualChat(entry.channel);
                            }}
                            aria-pressed={!entryChatHidden}
                          >
                            {entryChatHidden ? strings.LIVE.SHOW_CHAT : strings.LIVE.HIDE_CHAT}
                          </button>
                          <CommonTwitchLink
                            href={entry.link}
                            label={strings.LIVE.FOLLOW_TWITCH}
                            className="text-[0.72rem]"
                          />
                        </div>
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            <>
              <div
                className={[
                  "grid w-full gap-[0.75rem]",
                  hasMultipleEmbeds ? "grid-cols-2 max-[1100px]:grid-cols-1" : "grid-cols-1",
                  expanded && chatOpen && "max-[900px]:h-full max-[900px]:min-h-0",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {renderedEmbedEntries.map((entry) => (
                  <div
                    key={entry.channel}
                    className={[
                      "flex-[1_1_auto] min-w-0 w-full relative",
                      expanded
                        ? "aspect-auto h-full min-h-0"
                        : "aspect-video livestream-inline-player",
                      !expanded && chatOpen && "max-[900px]:h-auto",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {isPoppedOut ? (
                      <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center gap-[0.75rem] p-6 rounded-[1.7rem] text-center bg-[radial-gradient(circle_at_top,rgba(145,70,255,0.16),transparent_55%),#0b0b12]">
                        <p className="m-0 max-w-[18rem] text-[#8b8b9a] text-[0.9rem]">
                          {strings.LIVE.POPOUT_ACTIVE}
                        </p>
                        <button
                          type="button"
                          className="livestream-action-btn livestream-action-btn--primary"
                          onClick={closePopout}
                        >
                          {strings.LIVE.DOCK}
                        </button>
                      </div>
                    ) : (
                      <div
                        className={
                          expanded
                            ? "absolute inset-0 z-[2] w-full h-full aspect-auto"
                            : "absolute inset-0 z-[2] w-full h-full aspect-video"
                        }
                      >
                        <iframe
                          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                          src={entry.src}
                          title={`Finnhouse Live Stream ${entry.channel}`}
                          allow="autoplay; fullscreen"
                          className={
                            expanded
                              ? "absolute inset-0 w-full h-full border-none p-[0.5rem] rounded-[0.75rem] box-border"
                              : "absolute inset-0 w-full h-full border-none p-[0.65rem] rounded-[1.1rem] box-border"
                          }
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {chatOpen && !isPoppedOut ? <LivestreamChatSlot expanded={expanded} /> : null}
            </>
          )}
        </div>

        <footer
          className={[
            "flex items-center justify-between gap-[0.75rem] w-full rounded-[0.75rem] border border-[rgba(44,137,225,0.25)] bg-[rgba(15,23,42,0.65)] box-border",
            expanded ? "p-[0.75rem_1rem] max-w-none rounded-[0.5rem]" : "p-[0.55rem_0.85rem]",
            "max-[900px]:flex-col max-[900px]:items-stretch max-[900px]:gap-[0.65rem] max-[900px]:p-[0.65rem_0.75rem]",
          ].join(" ")}
        >
          <div className="flex items-center gap-[0.65rem] min-w-0 flex-1 max-[900px]:justify-center max-[900px]:flex-wrap">
            {isLive ? (
              <>
                <span className="inline-flex items-center shrink-0 px-[0.5rem] py-[0.15rem] rounded-full bg-red-500/[0.18] border border-red-500/[0.45] text-[#fca5a5] text-[0.72rem] font-bold tracking-[0.05em] uppercase">
                  {strings.LIVE.LIVE_BADGE}
                </span>
                <span className="text-white/85 text-[0.92rem] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                  {primaryStreamerLabel}
                </span>
                {viewerLabel ? (
                  <span className="text-[#c4b5fd] text-[0.92rem] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                    {viewerLabel}
                  </span>
                ) : null}
              </>
            ) : (
              <CommonTwitchLink
                href={primaryStreamLink}
                label={strings.LIVE.FOLLOW_TWITCH}
                className="text-[0.92rem]"
              />
            )}
          </div>

          <div className="flex items-center gap-[0.5rem] shrink-0 max-[900px]:w-full">
            {showDualLayout ? null : (
              <button
                type="button"
                className="livestream-action-btn max-[900px]:flex-[1_1_0] max-[900px]:text-center"
                onClick={toggleChat}
                aria-pressed={chatOpen}
              >
                {chatOpen ? strings.LIVE.HIDE_CHAT : strings.LIVE.SHOW_CHAT}
              </button>
            )}
            {popoutSupported && !expanded ? (
              <button
                type="button"
                className="livestream-action-btn max-[900px]:flex-[1_1_0] max-[900px]:text-center"
                onClick={showDualLayout ? openDualPopout : togglePopout}
                aria-pressed={showDualLayout ? false : isPoppedOut}
              >
                {showDualLayout
                  ? strings.LIVE.POP_OUT
                  : isPoppedOut
                    ? strings.LIVE.DOCK
                    : strings.LIVE.POP_OUT}
              </button>
            ) : null}
            <button
              type="button"
              className="livestream-action-btn livestream-action-btn--primary max-[900px]:flex-[1_1_0] max-[900px]:text-center"
              onClick={toggleExpanded}
              aria-pressed={expanded}
            >
              {expanded ? strings.LIVE.COLLAPSE : strings.LIVE.EXPAND}
            </button>
          </div>
        </footer>

        {showLoadingHint ? (
          <p className="m-0 min-h-6 text-[#8b8b9a] text-[0.95rem]">{strings.LIVE.LOADING}</p>
        ) : (
          <p className="m-0 min-h-6 invisible text-[0.95rem]" aria-hidden="true">
            {strings.LIVE.LOADING}
          </p>
        )}
      </div>
    </section>
  );
};
